import React, { useState } from 'react';
import {
  Book,
  Building,
  Users,
  Eye,
  Search,
  Target,
  Globe,
  Phone,
  ChevronDown,
  ChevronUp,
  Mail,
  Crown,
  Lightbulb,
  Zap,
  TrendingUp,
  MessageCircle
} from 'lucide-react';

const DocumentationPage = () => {
  const [activeTab, setActiveTab] = useState('howtostart');
  const [expandedAccordion, setExpandedAccordion] = useState({
    'howtostart': 'accordion-1',
    'brands': 'accordion-1',
    'affiliate-recruitment': 'accordion-1',
    'competitor-spy': 'accordion-1',
    'affiliate-gap': 'accordion-1',
    'keyword-explorer': 'accordion-1',
    'serp-checker': 'accordion-1',
    'contact-finder': 'accordion-1'
  });

  const toggleAccordion = (tab, accordionId) => {
    setExpandedAccordion(prev => ({
      ...prev,
      [tab]: prev[tab] === accordionId ? '' : accordionId
    }));
  };

  const tabs = [
    { id: 'howtostart', label: 'How To Start', icon: Lightbulb },
    { id: 'brands', label: 'Brands', icon: Building },
    { id: 'affiliate-recruitment', label: 'Affiliate Recruitment', icon: Users },
    { id: 'competitor-spy', label: 'Competitor Spy', icon: Eye },
    { id: 'affiliate-gap', label: 'Affiliate Gap', icon: Target },
    { id: 'keyword-explorer', label: 'Keyword Explorer', icon: Search },
    { id: 'serp-checker', label: 'SERP Checker', icon: TrendingUp },
    { id: 'contact-finder', label: 'Contact Finder', icon: MessageCircle }
  ];

  const AccordionItem = ({ id, title, children, isOpen, onToggle }) => (
    <div className="border border-gray-200 dark:border-slate-700 rounded-lg mb-4 overflow-hidden">
      <button
        onClick={onToggle}
        className="w-full px-6 py-4 text-left bg-white dark:bg-slate-900 hover:bg-gray-50 dark:hover:bg-slate-800 transition-colors flex items-center justify-between"
      >
        <span className="font-semibold text-gray-800 dark:text-slate-100">{title}</span>
        {isOpen ? <ChevronUp className="w-5 h-5 text-gray-600 dark:text-slate-400" /> : <ChevronDown className="w-5 h-5 text-gray-600 dark:text-slate-400" />}
      </button>
      {isOpen && (
        <div className="px-6 py-4 bg-gray-50 dark:bg-slate-800 border-t border-gray-200 dark:border-slate-700">
          <div className="prose prose-sm max-w-none text-gray-700 dark:text-slate-300">
            {children}
          </div>
        </div>
      )}
    </div>
  );

  const renderHowToStartTab = () => (
    <div className="space-y-4">
      <AccordionItem
        id="accordion-1"
        title="How to start?"
        isOpen={expandedAccordion['howtostart'] === 'accordion-1'}
        onToggle={() => toggleAccordion('howtostart', 'accordion-1')}
      >
        <p>
          MARS is built to help affiliate managers in the iGaming industry stay organized, work faster, and drive better results. 
          With a tailor-made list of affiliates ready within the platform, MARS streamlines your workflow, so you can focus on 
          maximizing partnerships and revenue. Whether you're managing relationships, tracking performance, or optimizing your 
          affiliate strategy, MARS provides a clean, intuitive platform designed for efficiency.
        </p>
        <p className="mt-4">
          This guide will walk you through MARS' key features and help you get set up quickly—so you can spend less time 
          managing and more time growing your affiliate network. Let's dive in!
        </p>
      </AccordionItem>

      <AccordionItem
        id="accordion-2"
        title="Demo MARS?"
        isOpen={expandedAccordion['howtostart'] === 'accordion-2'}
        onToggle={() => toggleAccordion('howtostart', 'accordion-2')}
      >
        <p>
          To explore the features and workflows in MARS, request access to our demo instance by emailing{' '}
          <a href="mailto:mars@mediacle.com" className="text-blue-600 hover:underline">mars@mediacle.com</a>.{' '}
          The demo provides a hands-on experience of how MARS can streamline affiliate management in the iGaming industry.
        </p>
        <p className="mt-4 font-medium text-blue-600">
          Get started today and see how MARS can optimize your workflow!
        </p>
      </AccordionItem>
    </div>
  );

  const renderBrandsTab = () => (
    <div className="space-y-4">
      <AccordionItem
        id="accordion-1"
        title="Overview"
        isOpen={expandedAccordion['brands'] === 'accordion-1'}
        onToggle={() => toggleAccordion('brands', 'accordion-1')}
      >
        <p>
          Brands is the central space where you can access detailed insights on a specific brand's affiliate presence and performance. 
          It shows how many affiliate sites a brand is currently listed on, identifies new opportunities, and provides key metrics 
          such as the MARS Score—our proprietary rating that evaluates listing effectiveness. Additionally, Brands offers an estimate 
          of the traffic a brand is generating, helping you make data-driven decisions.
        </p>
      </AccordionItem>

      <AccordionItem
        id="accordion-2"
        title="Create a Brand"
        isOpen={expandedAccordion['brands'] === 'accordion-2'}
        onToggle={() => toggleAccordion('brands', 'accordion-2')}
      >
        <p>
          You can create a new brand directly from the dashboard by clicking the + button. This opens a prompt where you can enter 
          key details, including the brand name and relevant attributes. While only the brand name is required, adding more details 
          ensures better tracking and analysis.
        </p>
      </AccordionItem>

      <AccordionItem
        id="accordion-3"
        title="Deleting a Brand"
        isOpen={expandedAccordion['brands'] === 'accordion-3'}
        onToggle={() => toggleAccordion('brands', 'accordion-3')}
      >
        <p>
          Currently, we do not support direct deletion of brands. To request deletion, please contact{' '}
          <a href="mailto:mars@mediacle.com" className="text-blue-600 hover:underline">mars@mediacle.com</a>.
        </p>
      </AccordionItem>
    </div>
  );

  const renderAffiliateRecruitmentTab = () => (
    <div className="space-y-4">
      <AccordionItem
        id="accordion-1"
        title="Overview"
        isOpen={expandedAccordion['affiliate-recruitment'] === 'accordion-1'}
        onToggle={() => toggleAccordion('affiliate-recruitment', 'accordion-1')}
      >
        <p>
          Affiliate Recruitment is your central hub for discovering and managing affiliate partnerships. It provides access to a 
          comprehensive list of affiliates in our system, allowing you to filter, analyze, and reach out to potential partners 
          effortlessly. With advanced filtering options, you can refine your search based on industry verticals, location, and 
          existing engagements, ensuring that you focus only on the most relevant opportunities.
        </p>
      </AccordionItem>

      <AccordionItem
        id="accordion-2"
        title="Filtering Affiliates"
        isOpen={expandedAccordion['affiliate-recruitment'] === 'accordion-2'}
        onToggle={() => toggleAccordion('affiliate-recruitment', 'accordion-2')}
      >
        <div>
          <p className="font-semibold mb-4">To streamline your search, Affiliate Recruitment offers multiple filtering options:</p>
          
          <div className="space-y-6">
            <div>
              <h4 className="font-semibold text-gray-800 mb-2">1. Vertical-Based Filtering</h4>
              <p className="mb-3">You can filter affiliates based on the industry they operate in, ensuring you connect with partners who align with your brand's focus. Available verticals include:</p>
              <ul className="list-disc list-inside space-y-1 ml-4">
                <li>Casino</li>
                <li>Sports Betting</li>
                <li>Lottery</li>
                <li>Poker</li>
                <li>Bingo</li>
              </ul>
              <p className="mt-3">Selecting one or more of these categories refines the list to show only affiliates who specialize in the chosen verticals.</p>
            </div>

            <div>
              <h4 className="font-semibold text-gray-800 mb-2">2. Country-Based Filtering</h4>
              <p>If you are targeting affiliates in specific regions, you can filter the list by country. This helps in narrowing down partners who are best suited for your market strategy and regulatory requirements.</p>
            </div>

            <div>
              <h4 className="font-semibold text-gray-800 mb-2">3. Existing Affiliates Filter</h4>
              <p className="mb-2">This filter helps you remove affiliates that are already working with any of your brands. By applying this, you can:</p>
              <ul className="list-disc list-inside space-y-1 ml-4">
                <li>Avoid duplication and redundant outreach.</li>
                <li>Focus on acquiring new partnerships rather than engaging with affiliates already in your network.</li>
              </ul>
            </div>

            <div>
              <h4 className="font-semibold text-gray-800 mb-2">4. Already Approached Filter</h4>
              <p>If you or your team have already reached out to an affiliate, this filter allows you to track past engagements. This prevents repeated outreach, making your recruitment efforts more efficient.</p>
            </div>
          </div>
        </div>
      </AccordionItem>

      <AccordionItem
        id="accordion-3"
        title="Affiliate Contact Information"
        isOpen={expandedAccordion['affiliate-recruitment'] === 'accordion-3'}
        onToggle={() => toggleAccordion('affiliate-recruitment', 'accordion-3')}
      >
        <p>
          Each affiliate listed in the system includes <strong>email contact details</strong>. This ensures that you have direct 
          communication channels available, enabling faster outreach and engagement.
        </p>
      </AccordionItem>

      <AccordionItem
        id="accordion-4"
        title="Managing Affiliate Recruitment"
        isOpen={expandedAccordion['affiliate-recruitment'] === 'accordion-4'}
        onToggle={() => toggleAccordion('affiliate-recruitment', 'accordion-4')}
      >
        <ol className="list-decimal list-inside space-y-2">
          <li><strong>Search & Filter:</strong> Use filters to refine your affiliate list based on industry, location, and existing interactions.</li>
          <li><strong>Review Contact Details:</strong> Access email information for easy outreach.</li>
          <li><strong>Track Engagements:</strong> Use the 'Already Approached' filter to check if an affiliate has been contacted before.</li>
          <li><strong>Build & Expand Your Network:</strong> Focus on new opportunities while avoiding duplication in outreach.</li>
        </ol>
      </AccordionItem>
    </div>
  );

  const renderCompetitorSpyTab = () => (
    <div className="space-y-4">
      <AccordionItem
        id="accordion-1"
        title="Track where your competitors are promoting their brands"
        isOpen={expandedAccordion['competitor-spy'] === 'accordion-1'}
        onToggle={() => toggleAccordion('competitor-spy', 'accordion-1')}
      >
        <div>
          <p className="mb-3">Competitor Spy allows you to analyze any competitor's affiliate presence by simply entering their URL. This feature helps you:</p>
          <ul className="list-disc list-inside space-y-1 ml-4">
            <li>Identify the websites and affiliates promoting your competitor's brand.</li>
            <li>Understand their marketing reach and placement strategies.</li>
            <li>Find potential affiliates that might also be interested in listing your brand.</li>
          </ul>
        </div>
      </AccordionItem>

      <AccordionItem
        id="accordion-2"
        title="How to Use Competitor Spy"
        isOpen={expandedAccordion['competitor-spy'] === 'accordion-2'}
        onToggle={() => toggleAccordion('competitor-spy', 'accordion-2')}
      >
        <div>
          <ol className="list-decimal list-inside space-y-2 mb-4">
            <li>Navigate to the Competitor Spy section.</li>
            <li>Enter the competitor's URL.</li>
            <li>The system will generate a report showing where the competitor's brand is being promoted.</li>
            <li>Review the list of websites and affiliate platforms they are using.</li>
          </ol>
          <p>Use this data to refine your affiliate strategy and explore new partnerships.</p>
        </div>
      </AccordionItem>
    </div>
  );

  const renderAffiliateGapTab = () => (
    <div className="space-y-4">
      <AccordionItem
        id="accordion-1"
        title="What Affiliate Gap do?"
        isOpen={expandedAccordion['affiliate-gap'] === 'accordion-1'}
        onToggle={() => toggleAccordion('affiliate-gap', 'accordion-1')}
      >
        <div>
          <p className="mb-3">Affiliate Gap allows you to compare your brand's affiliate presence against a competitor by entering their URL. It highlights:</p>
          <ul className="list-disc list-inside space-y-1 ml-4">
            <li>Affiliates working with your competitor, but not yet with your brand.</li>
            <li>Potential new sites where you can list your brand.</li>
            <li>Gaps in your affiliate strategy that can be optimized.</li>
          </ul>
        </div>
      </AccordionItem>

      <AccordionItem
        id="accordion-2"
        title="How to Use Affiliate Gap?"
        isOpen={expandedAccordion['affiliate-gap'] === 'accordion-2'}
        onToggle={() => toggleAccordion('affiliate-gap', 'accordion-2')}
      >
        <div>
          <ol className="list-decimal list-inside space-y-2 mb-4">
            <li>Navigate to the Affiliate Gap section.</li>
            <li>Enter your brand's URL and the competitor's URL.</li>
            <li>The system will generate a comparison report.</li>
            <li>Analyze the list of affiliates working with your competitor but not with your brand.</li>
            <li>Reach out to these affiliates to expand your network.</li>
          </ol>
          <p>By leveraging Competitor Spy and Affiliate Gap, you can strengthen your affiliate strategy, optimize your outreach, and stay ahead in the iGaming industry.</p>
        </div>
      </AccordionItem>
    </div>
  );

  const renderKeywordExplorerTab = () => (
    <div className="space-y-4">
      <AccordionItem
        id="accordion-1"
        title="What is keyword explorer?"
        isOpen={expandedAccordion['keyword-explorer'] === 'accordion-1'}
        onToggle={() => toggleAccordion('keyword-explorer', 'accordion-1')}
      >
        <p>
          Keyword Explorer helps you analyze keyword performance by providing search volume, cost-per-click (CPC), competition 
          difficulty, and related keyword suggestions. By entering a keyword and selecting a country, you can access key data 
          that helps you refine your SEO and affiliate marketing strategies.
        </p>
      </AccordionItem>

      <AccordionItem
        id="accordion-2"
        title="What Keyword Explorer Provides?"
        isOpen={expandedAccordion['keyword-explorer'] === 'accordion-2'}
        onToggle={() => toggleAccordion('keyword-explorer', 'accordion-2')}
      >
        <div>
          <p className="mb-3">For any keyword + country combination, Keyword Explorer delivers:</p>
          <ul className="list-disc list-inside space-y-2 ml-4">
            <li><strong>Volume:</strong> The number of monthly searches on Google for the selected keyword in the chosen country.</li>
            <li><strong>CPC (Cost-Per-Click):</strong> The estimated cost advertisers pay per click for the keyword in Google Ads.</li>
            <li><strong>Score:</strong> A measure of how hard it is to rank for the keyword as an affiliate.</li>
            <li><strong>Related Keywords:</strong> A list of similar keywords with their search volume, CPC, and difficulty scores.</li>
          </ul>
        </div>
      </AccordionItem>

      <AccordionItem
        id="accordion-3"
        title="How to Use Keyword Explorer?"
        isOpen={expandedAccordion['keyword-explorer'] === 'accordion-3'}
        onToggle={() => toggleAccordion('keyword-explorer', 'accordion-3')}
      >
        <ul className="list-disc list-inside space-y-2 ml-4">
          <li><strong>Enter a Keyword:</strong> Type the keyword you want to research.</li>
          <li><strong>Select a Country:</strong> Choose the country where you want data for that keyword.</li>
          <li><strong>Analyze the Data:</strong> Get insights on search volume, CPC, and ranking difficulty.</li>
          <li><strong>Explore Related Keywords:</strong> Use suggested keywords to expand your strategy.</li>
        </ul>
      </AccordionItem>
    </div>
  );

  const renderSerpCheckerTab = () => (
    <div className="space-y-4">
      <AccordionItem
        id="accordion-1"
        title="What is SERP Checker?"
        isOpen={expandedAccordion['serp-checker'] === 'accordion-1'}
        onToggle={() => toggleAccordion('serp-checker', 'accordion-1')}
      >
        <p>
          SERP Checker helps you monitor real-time search engine results pages (SERP) by entering a keyword and selecting a country. 
          This allows you to analyze rankings, competition, and opportunities specific to different regions. Whether you're tracking 
          your brand, researching competitors, or identifying new keyword opportunities, SERP Checker provides accurate and up-to-date 
          insights to enhance your SEO and affiliate strategies.
        </p>
      </AccordionItem>

      <AccordionItem
        id="accordion-2"
        title="How to Use SERP Checker?"
        isOpen={expandedAccordion['serp-checker'] === 'accordion-2'}
        onToggle={() => toggleAccordion('serp-checker', 'accordion-2')}
      >
        <div>
          <p className="mb-3">For any keyword + country combination, SERP Checker delivers:</p>
          <ol className="list-decimal list-inside space-y-2 ml-4">
            <li><strong>Enter a Keyword:</strong> Input the keyword you want to track.</li>
            <li><strong>Select a Country:</strong> Choose the country you want to analyze SERP results for.</li>
            <li><strong>View SERP Rankings:</strong> See the latest rankings, including the websites currently ranking for that keyword in the selected country.</li>
            <li><strong>Compare and Optimize:</strong> Identify and spot opportunities, and refine your strategy to improve visibility.</li>
          </ol>
        </div>
      </AccordionItem>
    </div>
  );

  const renderContactFinderTab = () => (
    <div className="space-y-4">
      <AccordionItem
        id="accordion-1"
        title="What is Contact Finder?"
        isOpen={expandedAccordion['contact-finder'] === 'accordion-1'}
        onToggle={() => toggleAccordion('contact-finder', 'accordion-1')}
      >
        <p>
          Contact Finder helps you retrieve email addresses for affiliate websites when they are not readily available. If you have 
          an affiliate site but lack the contact information, this tool quickly scans and finds the relevant email, saving you time 
          and effort in outreach. Whether you're tracking your brand, researching competitors, or identifying new keyword opportunities, 
          Contact Finder provides accurate and up-to-date insights to enhance your SEO and affiliate strategies.
        </p>
      </AccordionItem>

      <AccordionItem
        id="accordion-2"
        title="How Contact Finder Works?"
        isOpen={expandedAccordion['contact-finder'] === 'accordion-2'}
        onToggle={() => toggleAccordion('contact-finder', 'accordion-2')}
      >
        <ol className="list-decimal list-inside space-y-2 ml-4">
          <li><strong>Enter the Affiliate Website URL:</strong> Provide the URL of the affiliate site you want to find contact details for.</li>
          <li><strong>Automated Search:</strong> The system scans the website and available data sources for an associated email.</li>
          <li><strong>Retrieve Email Address:</strong> If found, the email address will be displayed for you to use in outreach.</li>
        </ol>
      </AccordionItem>
    </div>
  );

  const getTabDescription = (tabId) => {
    const descriptions = {
      'howtostart': 'Get started with MARS and learn the basics',
      'brands': 'Manage and analyze your brand presence',
      'affiliate-recruitment': 'Find and connect with the right affiliates efficiently',
      'competitor-spy': 'Analyze competitor strategies and uncover new opportunities',
      'affiliate-gap': 'Compare your brand with competitors and identify missing opportunities',
      'keyword-explorer': 'Find the best keywords to target with detailed search insights',
      'serp-checker': 'Track the latest SERP updates by keyword and country',
      'contact-finder': 'Find missing affiliate contact details effortlessly'
    };
    return descriptions[tabId] || '';
  };

  const renderTabContent = () => {
    switch (activeTab) {
      case 'howtostart':
        return renderHowToStartTab();
      case 'brands':
        return renderBrandsTab();
      case 'affiliate-recruitment':
        return renderAffiliateRecruitmentTab();
      case 'competitor-spy':
        return renderCompetitorSpyTab();
      case 'affiliate-gap':
        return renderAffiliateGapTab();
      case 'keyword-explorer':
        return renderKeywordExplorerTab();
      case 'serp-checker':
        return renderSerpCheckerTab();
      case 'contact-finder':
        return renderContactFinderTab();
      default:
        return renderHowToStartTab();
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-slate-950">
      <div className="max-w-6xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="bg-white dark:bg-slate-900 rounded-xl shadow-sm border border-gray-200 dark:border-slate-700 p-6 mb-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-gray-800 dark:text-slate-100">MARS Documentation</h1>
              <p className="text-gray-600 dark:text-slate-400 mt-1">Complete guide to using the MARS affiliate management platform</p>
            </div>
            <div className="flex items-center gap-3">
              <div className="flex items-center gap-2 px-4 py-2 bg-blue-50 dark:bg-blue-900/30 border border-blue-200 dark:border-blue-800 rounded-lg">
                <Crown className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                <span className="text-blue-800 dark:text-blue-400 font-medium">Documentation</span>
              </div>
            </div>
          </div>
        </div>

        {/* Main Container - Single White Box */}
        <div className="bg-white dark:bg-slate-900 rounded-xl shadow-sm border border-gray-200 dark:border-slate-700">
          {/* Tabs Navigation */}
          <div className="px-6 py-4 border-b border-gray-200 dark:border-slate-700">
            <nav className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-4" aria-label="Tabs">
              {tabs.map((tab, index) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`relative flex flex-col items-center gap-2 p-3 text-sm font-medium transition-all duration-200 rounded-lg focus:outline-none ${
                    activeTab === tab.id
                      ? 'bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 border-2 border-blue-200 dark:border-blue-800'
                      : 'text-gray-500 dark:text-slate-400 hover:text-gray-700 dark:hover:text-slate-200 hover:bg-gray-50 dark:hover:bg-slate-800 border-2 border-transparent'
                  }`}
                >
                  {/* Icon */}
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center transition-colors ${
                    activeTab === tab.id
                      ? 'bg-blue-600 text-white'
                      : 'bg-gray-200 dark:bg-slate-700 text-gray-500 dark:text-slate-400'
                  }`}>
                    <tab.icon className="w-4 h-4" />
                  </div>

                  <span className="text-center leading-tight">{tab.label}</span>

                  {/* Active indicator */}
                  {activeTab === tab.id && (
                    <div className="absolute -bottom-1 left-1/2 transform -translate-x-1/2 w-2 h-2 bg-blue-600 rounded-full" />
                  )}
                </button>
              ))}
            </nav>
          </div>

          {/* Content Area with Sidebar */}
          <div className="grid grid-cols-1 lg:grid-cols-4">
            {/* Sidebar */}
            <div className="lg:col-span-1 border-r border-gray-200 dark:border-slate-700">
              <div className="p-6">
                <h3 className="font-semibold text-gray-800 dark:text-slate-100 mb-4">Quick Navigation</h3>
                <div className="space-y-2">
                  {tabs.map((tab) => (
                    <button
                      key={tab.id}
                      onClick={() => setActiveTab(tab.id)}
                      className={`w-full text-left px-3 py-2 rounded-lg text-sm transition-colors ${
                        activeTab === tab.id
                          ? 'bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 font-medium'
                          : 'text-gray-600 dark:text-slate-400 hover:bg-gray-50 dark:hover:bg-slate-800'
                      }`}
                    >
                      {tab.label}
                    </button>
                  ))}
                </div>

                {/* Contact Info */}
                <div className="mt-8 p-4 bg-gray-50 dark:bg-slate-800 rounded-lg border border-gray-200 dark:border-slate-700">
                  <h4 className="font-semibold text-gray-800 dark:text-slate-100 mb-2 flex items-center gap-2">
                    <Mail className="w-4 h-4" />
                    Need Help?
                  </h4>
                  <p className="text-sm text-gray-600 dark:text-slate-400 mb-3">
                    Contact our support team for assistance
                  </p>
                  <a
                    href="mailto:mars@mediacle.com"
                    className="inline-flex items-center gap-2 text-sm text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 hover:underline"
                  >
                    <Mail className="w-4 h-4" />
                    mars@mediacle.com
                  </a>
                </div>
              </div>
            </div>

            {/* Main Content */}
            <div className="lg:col-span-3">
              <div className="p-6">
                {/* Tab Header */}
                <div className="mb-6 pb-4 border-b border-gray-200 dark:border-slate-700">
                  <div className="flex items-center gap-3 mb-2">
                    {(() => {
                      const currentTab = tabs.find(tab => tab.id === activeTab);
                      return (
                        <>
                          <div className="w-10 h-10 bg-blue-600 rounded-full flex items-center justify-center">
                            {currentTab && <currentTab.icon className="w-5 h-5 text-white" />}
                          </div>
                          <div>
                            <h2 className="text-xl font-bold text-gray-800 dark:text-slate-100">{currentTab?.label}</h2>
                            <p className="text-gray-600 dark:text-slate-400">{getTabDescription(activeTab)}</p>
                          </div>
                        </>
                      );
                    })()}
                  </div>
                </div>

                {/* Tab Content */}
                {renderTabContent()}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DocumentationPage;